import * as React from 'react';
import { StatusChip } from '@/components/ui/status-chip';
import {
  ReportEditor,
  type EditorBlock,
  type EditorCitation,
  type EditorSection,
  type ReportEditorCalls,
} from '@/components/report-editor';
import type { ReportBlock } from '@/lib/api/vendor-intelligence-report';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import { TOKEN_SOURCE } from './report-logic';
import {
  NO_NEEDS_PAGE_NOTE,
  blocksOfRole,
  coverClientLine,
  coverTiles,
  hasNeedsPage,
  isVendorPage,
  pageEyebrow,
  productOf,
  rankLine,
  slotLabel,
  toEditorBlocks,
  type ReportModel,
  type ReportPageModel,
} from './report-model';
import {
  CoverTiles,
  MethodSources,
  NeedsTable,
  RankingFigure,
  RequirementsByAreaTable,
  SensitivityByVendorTable,
  SetAsideTable,
  blockVendorName,
} from './pages/code-tables';
import { PageFrame, PageHeading, PageLede, PageNote } from './pages/frame';
import { ReportAreaMatrix } from './pages/report-area-matrix';

/**
 * One page of the report on screen (T-0283; E24-design §5, §9;
 * report-contract v2): the page's code tables and figures, laid out as the
 * PDF prints them, and its blocks in the shared editor (edit, rewrite,
 * revert), each placed by its role under the heading its slot names. A
 * published version renders the same pages read-only.
 */
export interface ReportPageEditing extends ReportEditorCalls {
  readOnly: boolean;
  onOpenCitation?: (citation: EditorCitation, block: EditorBlock) => void;
}

export interface ReportPageViewProps {
  model: ReportModel;
  page: ReportPageModel;
  index: number;
  count: number;
  editing: ReportPageEditing;
  engagement?: { client_name: string; name: string } | null;
}

const ofKind = (blocks: ReportBlock[], kind: ReportBlock['kind']) =>
  blocks.filter((b) => b.kind === kind);
const paragraphs = (blocks: ReportBlock[]) => ofKind(blocks, 'paragraph');

export function ReportPageView({
  model,
  page,
  index,
  count,
  editing,
  engagement,
}: ReportPageViewProps) {
  const { readOnly, onRewrite, onSave, onRevert, onOpenCitation } = editing;
  const editor = (sections: EditorSection[], className?: string) =>
    sections.some((s) => s.blocks.length > 0 || s.before || s.after) ? (
      <ReportEditor
        aria-label={`${page.title} text`}
        sections={sections}
        tokens={model.tokens}
        tokenSource={TOKEN_SOURCE}
        readOnly={readOnly}
        onRewrite={onRewrite}
        onSave={onSave}
        onRevert={onRevert}
        onOpenCitation={onOpenCitation}
        className={cn('gap-3', className)}
      />
    ) : null;
  const blocks = (list: ReportBlock[]) => toEditorBlocks(model, list);
  const section = (
    key: string,
    title: string,
    list: ReportBlock[],
    extra: Partial<EditorSection> = {},
  ): EditorSection => ({
    key: `${page.key}:${key}`,
    title,
    titleHidden: true,
    blocks: blocks(list),
    ...extra,
  });
  const role = (name: string, fallback: (blocks: ReportBlock[]) => ReportBlock[]) =>
    blocksOfRole(page, name, fallback);
  const label = (name: string) => slotLabel(model, page, name);
  const heading = (name: string, list: ReportBlock[]) =>
    list.length > 0 ? <PageHeading>{label(name)}</PageHeading> : undefined;
  const rest = (taken: ReportBlock[][]) =>
    page.blocks.filter((b) => !taken.some((t) => t.includes(b)));
  const eyebrow = pageEyebrow(model, page);
  const frame = (children: React.ReactNode, title?: string) => (
    <PageFrame index={index} count={count} eyebrow={eyebrow} title={title}>
      {children}
    </PageFrame>
  );

  switch (page.key) {
    case 'cover': {
      const date = model.at ? formatDate(model.at) : '';
      const headline = role('cover_headline', (b) => ofKind(b, 'headline'));
      return (
        <PageFrame
          index={index}
          count={count}
          eyebrow={`Vendor intelligence report${date ? ` · ${date}` : ''}`}
          className="min-h-full"
        >
          {editor([section('headline', 'Headline', headline)])}
          <p className="m-0 text-body text-ink-700">{coverClientLine(model, engagement)}</p>
          <div>
            {model.kind === 'draft' ? (
              <StatusChip tone="amber">Draft for review · version {model.version}</StatusChip>
            ) : (
              <StatusChip tone="frozen">Published · version {model.version}</StatusChip>
            )}
          </div>
          {editor([section('rest', 'Cover text', rest([headline]))])}
          <CoverTiles tiles={coverTiles(model)} />
        </PageFrame>
      );
    }
    case 'summary': {
      const verdict = role('verdict', (b) => ofKind(b, 'headline'));
      const lead = role('summary', (b) => paragraphs(b).slice(0, 1));
      const cards = ofKind(page.blocks, 'vendor_card');
      const why = role('why_these', (b) => paragraphs(b).slice(1));
      return frame(
        <>
          {editor([
            section('lead', 'Verdict', [...verdict, ...lead]),
            section('cards', 'The shortlisted vendors', cards, {
              className: `grid gap-3 ${cards.length === 4 ? 'md:grid-cols-2' : 'md:grid-cols-3'}`,
            }),
            section('why', label('why_these'), why, {
              before: heading('why_these', why),
              after: hasNeedsPage(model) ? undefined : (
                <PageNote>{model.tables?.needs?.note ?? NO_NEEDS_PAGE_NOTE}</PageNote>
              ),
            }),
            section('rest', 'More', rest([verdict, lead, cards, why])),
          ])}
        </>,
      );
    }
    case 'told_us':
      return frame(
        <>
          <PageLede>
            Each row joins what staff said in discovery to the requirements written for it and to
            the confirmed knowledge-base items that speak to it. Built from confirmed Discovery
            evidence; nothing on this page is drafted.
          </PageLede>
          <NeedsTable model={model} />
          {editor([section('rest', 'Text', page.blocks)])}
        </>,
        'Needs from discovery, traced to the RTM and the evidence',
      );
    case 'requirements': {
      const req = model.fit.requirements;
      const areas = model.tables?.requirements_by_area?.areas ?? (model.fit.areas ?? []).length;
      const signals = role('scope_signals', paragraphs);
      return frame(
        <>
          {req ? (
            <PageLede>
              The RTM holds {req.weighted} weighted requirements in {areas} client{' '}
              {areas === 1 ? 'area' : 'areas'}: {req.critical} critical and {req.desired} desired.
              Each area is weighted by twice its critical count plus its desired count; vendors are
              judged on the areas that matter most.
            </PageLede>
          ) : null}
          <RequirementsByAreaTable model={model} />
          {editor([
            section('signals', label('scope_signals'), signals, {
              before: heading('scope_signals', signals),
            }),
            section('rest', 'More', rest([signals])),
          ])}
        </>,
        'What the RTM weighs most heavily',
      );
    }
    case 'matrix':
      return frame(
        <>
          <ReportAreaMatrix model={model} />
          {editor([section('rest', 'Text', page.blocks)])}
        </>,
        'Confirmed evidence against each client area',
      );
    case 'ranking': {
      const changes = role('what_changes', paragraphs);
      return frame(
        <>
          <PageLede>
            Fit index out of 100, from confirmed ratings only. The shortlisted vendors are marked.
          </PageLede>
          <RankingFigure model={model} />
          <PageHeading>Does the order depend on the scoring choices?</PageHeading>
          <SensitivityByVendorTable model={model} />
          {editor([
            section('change', label('what_changes'), changes, {
              before: heading('what_changes', changes),
            }),
            section('rest', 'More', rest([changes])),
          ])}
        </>,
        'The ranking, and whether it holds',
      );
    }
    case 'set_aside': {
      const why = page.blocks.filter((b) => b.product_id !== null);
      const lede = page.blocks.filter((b) => b.product_id === null);
      const note =
        model.tables?.set_aside?.note ??
        (model.cover?.assessed_count !== undefined
          ? `${model.cover.products_considered ?? model.fit.products.length} vendors in scope; ${model.cover.assessed_count} assessed in depth against every client area.`
          : null);
      const whyBlocks = toEditorBlocks(model, why).map((b, i) => ({
        ...b,
        group: { id: why[i]!.product_id ?? '', title: blockVendorName(model, why[i]!) },
      }));
      return frame(
        <>
          {note ? <PageLede>{note}</PageLede> : null}
          {editor([section('lede', 'Text', lede)])}
          <SetAsideTable model={model} />
          {whyBlocks.length > 0 ? (
            <ReportEditor
              aria-label="Why each vendor is set aside"
              sections={[
                {
                  key: `${page.key}:why`,
                  title: 'Why',
                  titleHidden: true,
                  before: <PageHeading>Why</PageHeading>,
                  blocks: whyBlocks,
                },
              ]}
              tokens={model.tokens}
              tokenSource={TOKEN_SOURCE}
              readOnly={readOnly}
              onRewrite={onRewrite}
              onSave={onSave}
              onRevert={onRevert}
              onOpenCitation={onOpenCitation}
              className="gap-3"
            />
          ) : null}
        </>,
        'Why the other vendors are not on the shortlist',
      );
    }
    case 'method': {
      // Without roles, three template paragraphs are sources, how, limits; two are how, limits.
      const ps = paragraphs(page.blocks);
      const at = ps.length >= 3 ? 0 : -1;
      const sources = role('sources', () => (at === 0 ? ps.slice(0, 1) : []));
      const how = role('how_numbers', () => ps.slice(at + 1, at + 2));
      const limits = role('limits', () => ps.slice(at + 2, at + 3));
      const next = role('next_steps', (b) => ofKind(b, 'bullets'));
      return frame(
        <>
          {editor(
            [
              section('sources', label('sources'), sources, {
                before: (
                  <>
                    <PageHeading>{label('sources')}</PageHeading>
                    {sources.length === 0 ? <MethodSources model={model} /> : null}
                  </>
                ),
              }),
              section('how', label('how_numbers'), how, {
                before: <PageHeading>{label('how_numbers')}</PageHeading>,
              }),
              section('limits', label('limits'), limits, {
                before: <PageHeading>{label('limits')}</PageHeading>,
              }),
              section('next', label('next_steps'), next, {
                before: <PageHeading>{label('next_steps')}</PageHeading>,
              }),
              section('rest', 'More', rest([sources, how, limits, next]), {
                className: 'md:col-span-2',
              }),
            ],
            'grid gap-x-6 gap-y-4 md:grid-cols-2',
          )}
        </>,
        'How this was built, its limits, and what to do next',
      );
    }
    default: {
      if (isVendorPage(page.key)) {
        const product = productOf(model, page.product_id);
        const verdict = role('verdict', (b) => ofKind(b, 'headline'));
        const why = role('why', (b) => ofKind(b, 'bullets').slice(0, 1));
        const watch = role('watch_outs', (b) => ofKind(b, 'bullets').slice(1, 2));
        const scenarios = role('scenarios', (b) => ofKind(b, 'scenarios'));
        const line = rankLine(product);
        return frame(
          <>
            <h2 className="m-0 font-serif text-h1 font-semibold leading-tight text-ink-900">
              {product?.name ?? page.title}
            </h2>
            {line ? <PageNote>{line}</PageNote> : null}
            {editor(
              [
                section('verdict', 'Verdict', verdict, { className: 'md:col-span-2' }),
                section('why', label('why'), why, {
                  before: <PageHeading>{label('why')}</PageHeading>,
                }),
                section('watch', label('watch_outs'), watch, {
                  before: <PageHeading>{label('watch_outs')}</PageHeading>,
                }),
                section('more', 'More', rest([verdict, why, watch, scenarios]), {
                  className: 'md:col-span-2',
                }),
                section('scenarios', label('scenarios'), scenarios, {
                  className: 'md:col-span-2',
                  before: heading('scenarios', scenarios),
                }),
              ],
              'grid gap-x-6 gap-y-4 md:grid-cols-2',
            )}
          </>,
        );
      }
      // A page this layout does not know (a v1 draft's section): its blocks, with its title.
      return frame(editor([section('blocks', page.title, page.blocks)]), page.title);
    }
  }
}
